import React, { useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemText, Collapse, Typography, IconButton } from '@mui/material';
import { ExpandLess, ExpandMore, Close } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const drawerWidth = 260;

const RightSidebar = () => {
  const { selectedProjectId, setSelectedProjectId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projectName, setProjectName] = useState('');
  const [openDataAnalysis, setOpenDataAnalysis] = useState(true);
  const [openDataQuality, setOpenDataQuality] = useState(false);
  const [openDiscretization, setOpenDiscretization] = useState(false);
  const [openExtremes, setOpenExtremes] = useState(false);
  const [openRiskDiff, setOpenRiskDiff] = useState(false);
  const [openMRP, setOpenMRP] = useState(false);
  const [openCFProjection, setOpenCFProjection] = useState(false);
  const [openRiskQuant, setOpenRiskQuant] = useState(false);
  const [openMoCDownturn, setOpenMoCDownturn] = useState(false);

  useEffect(() => {
    if (selectedProjectId) {
      const token = localStorage.getItem('access_token');
      axios.get(`/api/v1/projects/${selectedProjectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setProjectName(res.data.name)).catch(console.error);
    }
  }, [selectedProjectId]);

  const handleModuleClick = (modulePath) => {
    if (!selectedProjectId) return;
    navigate(`/${modulePath}?project_id=${selectedProjectId}`);
  };

  const menuItemStyle = { borderRadius: 2, mx: 1, mb: 0.5, '&:hover': { bgcolor: '#4a5b6e' }, backgroundColor: '#34495e' };
  const submenuItemStyle = { pl: 4, ...menuItemStyle };
  const nestedSubmenuItemStyle = { pl: 6, ...menuItemStyle };

  if (!selectedProjectId) return null;

  return (
    <Box sx={{ 
      width: drawerWidth, 
      bgcolor: '#3a4a5a', 
      color: 'white', 
      borderLeft: '2px solid #1abc9c', 
      display: 'flex', 
      flexDirection: 'column', 
      flexShrink: 0,
      alignSelf: 'flex-start',
      position: 'sticky',
      top: 0
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: '#2c3e50' }}>
        <Typography variant="subtitle1">{projectName || 'Project'}</Typography>
        <IconButton size="small" onClick={() => { setSelectedProjectId(null); navigate('/'); }} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </Box>
      <List sx={{ p: 1, flexGrow: 1, overflowY: 'auto' }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenDataAnalysis(!openDataAnalysis)} sx={menuItemStyle}>
            <ListItemText primary="Data Analysis" />
            {openDataAnalysis ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openDataAnalysis} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setOpenDataQuality(!openDataQuality)} sx={submenuItemStyle}>
                <ListItemText primary="Data Quality" />
                {openDataQuality ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openDataQuality} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('missing-rate')} sx={nestedSubmenuItemStyle}><ListItemText primary="Missing rate" /></ListItemButton></ListItem>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('missing-imputation')} sx={nestedSubmenuItemStyle}><ListItemText primary="Missing imputation" /></ListItemButton></ListItem>
              </List>
            </Collapse>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setOpenDiscretization(!openDiscretization)} sx={submenuItemStyle}>
                <ListItemText primary="Discretization" />
                {openDiscretization ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openDiscretization} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('expert-cut')} sx={nestedSubmenuItemStyle}><ListItemText primary="Expert cut" /></ListItemButton></ListItem>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('isotonic-regression')} sx={nestedSubmenuItemStyle}><ListItemText primary="Isotonic regression" /></ListItemButton></ListItem>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('monotonic-lightgbm')} sx={nestedSubmenuItemStyle}><ListItemText primary="Monotonic Light GBM" /></ListItemButton></ListItem>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('monotonic-binning')} sx={nestedSubmenuItemStyle}><ListItemText primary="Monotonic Optimal Binning" /></ListItemButton></ListItem>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('quantile-cut')} sx={nestedSubmenuItemStyle}><ListItemText primary="Quantile cut" /></ListItemButton></ListItem>
              </List>
            </Collapse>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setOpenExtremes(!openExtremes)} sx={submenuItemStyle}>
                <ListItemText primary="Extremes values" />
                {openExtremes ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openExtremes} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('iqr')} sx={nestedSubmenuItemStyle}><ListItemText primary="IQR" /></ListItemButton></ListItem>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('isolation-forest')} sx={nestedSubmenuItemStyle}><ListItemText primary="Isolation Forest" /></ListItemButton></ListItem>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('zscore')} sx={nestedSubmenuItemStyle}><ListItemText primary="ZScore" /></ListItemButton></ListItem>
              </List>
            </Collapse>
          </List>
        </Collapse>

        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenRiskDiff(!openRiskDiff)} sx={menuItemStyle}>
            <ListItemText primary="Risk Diff" />
            {openRiskDiff ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openRiskDiff} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('sampling')} sx={submenuItemStyle}><ListItemText primary="Sampling" /></ListItemButton></ListItem>
            <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('cart')} sx={submenuItemStyle}><ListItemText primary="CART" /></ListItemButton></ListItem>
            <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('performance-testing')} sx={submenuItemStyle}><ListItemText primary="Performance & Testing" /></ListItemButton></ListItem>
          </List>
        </Collapse>

        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenMRP(!openMRP)} sx={menuItemStyle}>
            <ListItemText primary="Max Recovery Period" />
            {openMRP ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openMRP} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('mrp-cashflows')} sx={submenuItemStyle}><ListItemText primary="Cash flows" /></ListItemButton></ListItem>
            <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('mrp-numbers')} sx={submenuItemStyle}><ListItemText primary="Number" /></ListItemButton></ListItem>
          </List>
        </Collapse>

        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenCFProjection(!openCFProjection)} sx={menuItemStyle}>
            <ListItemText primary="CF Projection" />
            {openCFProjection ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openCFProjection} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('additive-projection')} sx={submenuItemStyle}><ListItemText primary="Additive model" /></ListItemButton></ListItem>
            <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('multiplicative-projection')} sx={submenuItemStyle}><ListItemText primary="Multiplicative model" /></ListItemButton></ListItem>
          </List>
        </Collapse>

        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenRiskQuant(!openRiskQuant)} sx={menuItemStyle}>
            <ListItemText primary="Risk quantification" />
            {openRiskQuant ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openRiskQuant} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('lra')} sx={submenuItemStyle}><ListItemText primary="LRA" /></ListItemButton></ListItem>
            <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('moc-c')} sx={submenuItemStyle}><ListItemText primary="MoC C" /></ListItemButton></ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setOpenMoCDownturn(!openMoCDownturn)} sx={submenuItemStyle}>
                <ListItemText primary="MoC downturn" />
                {openMoCDownturn ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openMoCDownturn} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('dwt-identification')} sx={nestedSubmenuItemStyle}><ListItemText primary="Dwt Identification" /></ListItemButton></ListItem>
                <ListItem disablePadding><ListItemButton onClick={() => handleModuleClick('dwt-quantification')} sx={nestedSubmenuItemStyle}><ListItemText primary="Dwt quantification" /></ListItemButton></ListItem>
              </List>
            </Collapse>
          </List>
        </Collapse>
      </List>
    </Box>
  );
};

export default RightSidebar;
